import { Button, ButtonProps, Dialog, ThemeProvider,styled } from '@mui/material'
import './SwitchClassPopup.scss';
import { FETCH_POLICY, INTER_FONT_FAMILY, theme } from '../../utils/constants';
import { FC, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { classroomObjectType } from '../../pages/AdminStudents/AdminStudentsClassroom';
import ToasterWithMessage from '../toaster';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import Select, { StylesConfig } from 'react-select';
import images from '../../assets/images';
import { GET_CLASSROOM_LIST, STUDENTS_SWITCH_CLASS } from '../../pages/AdminStudents/api/AdminStudentsQuery';
import { getScreenDimensions } from '../../utils/helper';
import { trackButtonClick } from '../../firebase/analyticsHelper';
import { ButtonClickEvents } from '../../firebase/constants/events';

interface propsType{
    open: boolean;
    setOpen: (val: boolean) => void;
    selectedClass: classroomObjectType | undefined;
    checkedStudents: string[];
    refetch: boolean;
    setRefetch: (val: boolean) => void;
}

interface StyledButtonProps extends ButtonProps {
    width?: string;
    variant?: "contained";
}

interface IFormInput {
    class: string;
}

interface classListType {
    value: string;
    label: string;
}

const { isMobileScreen } = getScreenDimensions();

//Yes Button Style
const YesStyledButton = styled(Button)<StyledButtonProps>(() => ({
    fontFamily: INTER_FONT_FAMILY,
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    border: "0",
    backgroundColor: "var(--color-steelblue)",
    borderRadius: isMobileScreen ? "20px" : "10px",
    height: "33px",
    width: '154px',
    display: "flex",
    color: '#ffffff',
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    textTransform: "none",
    '&:hover': {
        backgroundColor: "var(--color-steelblue)",
        color: '#ffffff',
    }
}));

//No Button Style
const NoStyledButton = styled(Button)<StyledButtonProps>(() => ({
    fontFamily: INTER_FONT_FAMILY,
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    border: "0",
    backgroundColor: "#F3F7FA",
    borderRadius: isMobileScreen ? "20px" : "10px",
    height: "33px",
    width: '154px',
    display: "flex",
    color: '#000000',
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    textTransform: "none",
    '&:hover': {
        backgroundColor: "#F3F7FA",
        color: '#000000',
    }
}));

const schema = yup.object().shape({
    class: yup.string().required("Class is required")
});

const customStyleSelect: StylesConfig = {
    control: (styles) => ({
      ...styles,
      cursor: 'pointer',
      height: '41px',
      width: '100%',
      padding: '0px',
      minHeight: '27px',
      border: 'none',
      backgroundColor: '#f0f3f5',
      outline: 'none'
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      display: 'none'
    }),
    indicatorsContainer: (styles) => ({
      ...styles,
      padding: '0px 10px 0px 8px'
    }),
    input: (styles) => ({
      ...styles,
      fontFamily: 'Inter',
      fontWeight: '400',
      fontSize: '12px',
    }),
    placeholder: (styles) => ({
      ...styles,
      fontFamily: 'Inter',
      fontWeight: '400',
      fontSize: '12px',
      color: '#B1B1B1',
      fontStyle: 'italic',
      overflow: 'hidden',
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    //   textTransform: 'capitalize'
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isFocused ? '#f5f5f5' : isSelected ? '#e6e6e6' : undefined,
      color: '#333',
      fontFamily: 'Inter',
      fontWeight: isSelected ? '600' : '400',
      fontSize: '12px',
      cursor: 'pointer'
    }),
    singleValue: (styles) => ({
      ...styles,
      color: '#333',
      fontFamily: 'Inter',
      fontWeight: '400',
      fontSize: '12px',
    //   textTransform: 'capitalize'
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      color: '#2D2D2D',
      padding: '0px'
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: '200px',
    //   textTransform: 'capitalize'
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '180px',
      overflowY: 'auto'
    }),
}
 
const SwitchClassPopup: FC<propsType> = ({ open, setOpen, selectedClass, checkedStudents, setRefetch, refetch }) => {
    
    const [classRoomList, setClassRoomList] = useState<classListType[]>();
    const { success, error } = ToasterWithMessage();
    const methods = useForm<IFormInput>({ resolver: yupResolver(schema), });
    const { getValues, handleSubmit, formState: { errors, isSubmitted }, clearErrors, reset } = methods;
    const { closeIcon } = images;

    const [getClassList, { loading }] = useLazyQuery(GET_CLASSROOM_LIST, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: (data) => {
            setClassRoomList(data?.allClassrooms?.nodes?.map((val: classroomObjectType) => ({label: val?.cOriginalName, value: val?.cId})));
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    const [studentsSwitchClass, { loading: swithClassLoading }] = useMutation(STUDENTS_SWITCH_CLASS, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: () => {
            success("Students moved successfully");
            setRefetch(!refetch);
        },
        onError: (err) => {
            error(err?.message);
        }
    })

    useEffect(() => {
        if (open) {
            getClassList({
                variables: {
                    orderBy: ["C_NAME_ASC"],
                    cId: selectedClass?.cId
                }
            })
        }
    }, [open])

    const onSubmitForm: SubmitHandler<IFormInput> = (data) => {
        trackButtonClick(ButtonClickEvents?.STUDENT_SHARE_MOVE_BUTTON_CLICK, {
            description : "User clicks the confirm move button",
        })
        if (checkedStudents.length > 0 && data?.class) {
            studentsSwitchClass({
                variables: {
                    fromCid: selectedClass?.cId,
                    toCid: data?.class,
                    sIds: checkedStudents
                }
            })
        }
        handleClose();
    }

    const handleClose = () => {
        trackButtonClick(ButtonClickEvents?.STUDENT_SHARE_DISCARD_BUTTON_CLICK, {
            description : "User clicks the discard button",
        })
        setOpen(false);
        reset();
    }

    return (
        <ThemeProvider theme={theme}>
            <Dialog open={open} classes={{paper: 'paperStyle'}} >
                <div className='switchClassMainContainer'>
                    <div className='closeContainer' onClick={handleClose}>
                        <img src={closeIcon} alt='closeIcon'/>
                    </div>
                    <div className='contentContainer'>
                        <div className='messageStyle'>
                            Move all students to the next class or you can select some students and move them.
                        </div>  
                        <div className='classListContainer'>
                            <form className='formStyle' id="switchClassForm" autoComplete="off" onSubmit={handleSubmit(onSubmitForm)}>
                                <label className='textareaLabelStyle'>Select Class</label>
                                <Select
                                onChange={(selectedOption) => {
                                    if (selectedOption) {
                                        const option = selectedOption as classListType;
                                        methods.setValue('class', option.value);
                                        clearErrors('class');
                                    } else {
                                        methods.setValue('class', '');
                                    }
                                }}
                                isLoading={loading}
                                placeholder={selectedClass?.cOriginalName}
                                value={classRoomList?.find(option => option?.value === getValues('class'))}
                                classNamePrefix="dropdown-indicator"
                                styles={customStyleSelect}
                                options={classRoomList}
                                />
                                {errors.class && isSubmitted && <span className="error">{errors?.class && errors?.class?.message}</span>}
                            </form>
                        </div>
                        <div className='ctaActions'>
                            <NoStyledButton 
                                variant='contained' 
                                className='noBtnStyle' 
                                disableElevation
                                onClick={handleClose}
                            >
                                Discard
                            </NoStyledButton>
                            <YesStyledButton 
                                variant='contained' 
                                className='yesBtnStyle'
                                disableElevation
                                type='submit'
                                form="switchClassForm"
                                disabled={swithClassLoading}
                            >
                                Move
                            </YesStyledButton>
                        </div>
                    </div>
                </div>
            </Dialog>
        </ThemeProvider>
    )
}

export default SwitchClassPopup;
